'use client'

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import type { PhonePbxConfig } from '@/lib/pbx/flow/types'

export function usePhonePbxConfigs() {
  const [phones, setPhones] = useState<PhonePbxConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const loadPhones = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth('/api/pbx/phones')
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to fetch phones')
      }
      const data = await res.json()
      setPhones(data.phones || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated])

  useEffect(() => {
    loadPhones()
  }, [loadPhones])

  return {
    phones,
    isLoading,
    error,
    refetch: loadPhones,
  }
}

export function usePhonePbxMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth } = useAuth()

  const enablePbx = useCallback(async (
    phoneNumber: string,
    phoneNumberSid: string,
    callFlowId: string
  ) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/phones/${encodeURIComponent(phoneNumber)}/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_flow_id: callFlowId,
          phone_number_sid: phoneNumberSid,
        }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to enable PBX')
      }

      const data = await res.json()
      return data.config as PhonePbxConfig
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const disablePbx = useCallback(async (phoneNumber: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/phones/${encodeURIComponent(phoneNumber)}/disable`, {
        method: 'POST',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to disable PBX')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const updateConfig = useCallback(async (phoneNumber: string, updates: Partial<PhonePbxConfig>) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/phones/${encodeURIComponent(phoneNumber)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update config')
      }

      const data = await res.json()
      return data.config as PhonePbxConfig
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  return {
    enablePbx,
    disablePbx,
    updateConfig,
    isLoading,
    error,
  }
}
